import { useTranslation } from 'react-i18next';
import { useRoutePending } from './routePendingContext';
import { useDelayedFlag } from './useDelayedFlag';

/**
 * How long a navigation must stay pending before the bar appears. Everything
 * that resolves faster than this — i.e. every navigation to an already-loaded
 * chunk — shows nothing, so the app does not strobe on ordinary clicks.
 */
export const ROUTE_PENDING_DELAY_MS = 180;

/**
 * Once the bar IS up, keep it up at least this long. A navigation that lands a
 * few milliseconds past the delay would otherwise blink.
 */
export const ROUTE_PENDING_MIN_VISIBLE_MS = 300;

/**
 * The app's only feedback that a route transition is under way.
 *
 * `v7_startTransition` means React holds the previous page on screen while the
 * destination route's lazy chunk loads, so a slow navigation otherwise looks
 * exactly like a dead click: the URL moves, nothing else does, and the user
 * clicks again. `PageSkeleton` cannot fill that gap — the `<Suspense>` boundary
 * that renders it is already mounted with content by the time any in-app
 * navigation happens, and React does not fall back to it for a suspended
 * transition. So the feedback has to come from the transition's own pending
 * state, which `TransitionRouter` publishes.
 *
 * Two channels, because a colour alone is not an announcement: a progress bar
 * pinned to the top of the viewport, and a polite live region that says the page
 * is loading. The live region is mounted unconditionally — a `role="status"`
 * added to the DOM at the same moment as its text is not reliably announced.
 */
export function RouteTransitionIndicator() {
  const { t } = useTranslation('common');
  const pending = useRoutePending();
  const visible = useDelayedFlag(pending, {
    delayMs: ROUTE_PENDING_DELAY_MS,
    minVisibleMs: ROUTE_PENDING_MIN_VISIBLE_MS,
  });

  return (
    <>
      {visible && (
        <div
          data-testid="route-transition-indicator"
          aria-hidden="true"
          // `h-1` is 4px: the rail ends at y=4, clear of the search pill's top
          // edge at y=13.5, and is `pointer-events-none` besides, so it could
          // not take a click even where it did overlap.
          // header-overlay-clearance-ok: 4px pointer-events-none rail above the header
          // `top-safe-top` keeps the rail visible on an edge-to-edge Android
          // shell, where y=0 is behind the status bar rather than at the top of
          // the app. It resolves to `top: 0` everywhere else.
          className="pointer-events-none fixed inset-x-0 top-safe-top z-toast h-1"
        >
          {/* `bg-primary` is what the shared `Progress` component fills with,
              and it is the only candidate that INVERTS with the theme: light
              `0 0% 17%` on the near-white shell, dark `210 40% 96%` on the
              near-black one. `bg-module-accent` was tried first — it is the
              design system's indication hue and prettier in light mode — but a
              module hue does not lighten for dark mode, and at this weight the
              Shipments blue measured ~3.4:1 on `--background` and read as
              almost nothing. */}
          <div className="route-transition-bar h-full bg-primary" />
        </div>
      )}
      <div
        data-testid="route-transition-status"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {visible ? t('routeTransition.loading') : null}
      </div>
    </>
  );
}
